import React from 'react';
import { Link } from 'react-router-dom';
import type { MenuItem, Restaurant } from '../../services/restaurantService';
import MenuItemOptionsSheet from '../common/MenuItemOptionsSheet/MenuItemOptionsSheet';
import MenuLinePrompt from '../common/MenuItemOptionsSheet/MenuLinePrompt';
import MenuItemPriceBlock from '../common/MenuItemPriceBlock/MenuItemPriceBlock';
import { MenuCardAddToCart, useMenuItemCartControls } from '../../hooks/useMenuItemCartControls';
import {
  getDisplayMrp,
  getDisplayPrice,
  getEffectiveDisplayPrice,
  getStrikeThroughPrice,
  getMenuOptionsMeta,
} from '../../utils/menuPricing';
import { DEFAULT_IMAGES, getMenuItemImageUrl } from '../../constants/images';
import { IconBookmark, IconBookmarkFilled } from '../RestaurantMenu/MenuListingIcons';
import { resolveTag } from '../../utils/menuTags';

export interface MenuItemCardProps {
  item: MenuItem;
  restaurant: Restaurant;
  onAddToCart: MenuCardAddToCart;
  onToggleWishlist: (menuId: number, currentState: boolean, wishlistId?: number) => void;
  formatPrice: (price: number) => string;
  /** undefined = ordering status not yet known (suppress controls while loading) */
  orderingEnabled?: boolean;
  showImages?: boolean;
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({
  item,
  restaurant,
  onAddToCart,
  onToggleWishlist,
  formatPrice,
  orderingEnabled,
  showImages = true,
}) => {
  const cart = useMenuItemCartControls(item, restaurant.appName, onAddToCart);
  const { quantity, selections, handleIncrement, handleDecrement } = cart;
  const optionsMeta = getMenuOptionsMeta(item);
  const openOptionsSheet = cart.openSheet;

  const sellingPrice = getDisplayPrice(item, {
    sizeTypeId: selections.sizeTypeId,
  });
  const effectivePrice = getEffectiveDisplayPrice(item, {
    sizeTypeId: selections.sizeTypeId,
  });
  const displayMrp = getDisplayMrp(item, selections.sizeTypeId);
  const strikeThroughPrice = getStrikeThroughPrice(item, {
    sizeTypeId: selections.sizeTypeId,
  });
  const isAvailable = item.availability !== false;

  const discountPercentage =
    strikeThroughPrice && strikeThroughPrice > effectivePrice
      ? Math.round(((strikeThroughPrice - effectivePrice) / strikeThroughPrice) * 100)
      : item.discountPercent != null && item.discountPercent > 0
        ? Math.round(item.discountPercent)
        : displayMrp && displayMrp > sellingPrice
          ? Math.round(((displayMrp - sellingPrice) / displayMrp) * 100)
          : item.discount != null && item.discount > 0
            ? Math.round(item.discount)
            : 0;

  // Ordering off (e.g. a menu-only plan): show the menu without qty controls or a
  // misleading "Out of Stock"; only items the restaurant marked unavailable get the label.
  const controls =
    orderingEnabled === undefined ? null : !isAvailable ? (
      <div className="out-of-stock-footer menu-item-controls-row">
        <span className="out-of-stock-label">Out of Stock</span>
      </div>
    ) : orderingEnabled ? (
      <div className="card-qty menu-item-controls-row">
        <div className="quantity-controls">
          <button type="button" className="qty-btn decrement" onClick={handleDecrement} disabled={quantity === 0} aria-label="Decrease quantity">−</button>
          <span className="qty-val qty-input" aria-live="polite">{quantity}</span>
          <button type="button" className="qty-btn increment" onClick={handleIncrement} aria-label="Increase quantity">+</button>
        </div>
      </div>
    ) : null;

  return (
    <div className={`menu-item-card menu-card ${!isAvailable ? 'out-of-stock-card' : ''} ${!showImages ? 'menu-card--no-image' : ''}`} id={`row_${item.id}`}>
      {showImages && (
        <div className="card-img-wrap menu-item-image">
          <span
            className={`card-veg-dot ${item.itemType === 'nonveg' ? 'non-veg' : ''}`}
            aria-label={item.itemType === 'veg' ? 'Vegetarian' : 'Non-vegetarian'}
          />
          <button
            type="button"
            className={`card-bookmark wishlist-btn ${item.inWishlist ? 'saved active' : ''}`}
            onClick={() => onToggleWishlist(item.id, item.inWishlist || false, item.wishlistId)}
            aria-label={item.inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            title="Save"
          >
            {item.inWishlist ? <IconBookmarkFilled size={14} /> : <IconBookmark size={14} />}
          </button>
          <Link to={`/restaurants/${restaurant.appName}/menu/${item.itemUrl || item.id}`}>
            <img
              src={
                restaurant?.id && item.id && item.imageBig
                  ? getMenuItemImageUrl(restaurant.id, item.id, item.imageBig)
                  : DEFAULT_IMAGES.MENU_ITEM
              }
              alt={item.item}
              className={!isAvailable ? 'out-of-stock-image' : ''}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target.src !== DEFAULT_IMAGES.MENU_ITEM) {
                  target.src = DEFAULT_IMAGES.MENU_ITEM;
                }
              }}
            />
          </Link>
        </div>
      )}

      <div className="card-body">
        {showImages ? (
          <>
            <MenuItemPriceBlock
              effectivePrice={effectivePrice}
              strikeThroughPrice={strikeThroughPrice}
              discountPercentage={discountPercentage}
              sellingPrice={sellingPrice}
              formatPrice={formatPrice}
            />
            <div className="card-name menu-item-title">
              <Link to={`/restaurants/${restaurant.appName}/menu/${item.itemUrl || item.id}`}>
                {item.item}
              </Link>
            </div>
            {item.tags && item.tags.length > 0 && (() => {
              const rt = resolveTag(item.tags![0]);
              return (
                <span className="card-tag-pill" style={{ background: rt.bg, color: rt.color, borderColor: rt.border }}>
                  <span>{rt.label}</span>
                  {rt.Icon && <rt.Icon size={10} />}
                </span>
              );
            })()}
            {item.promotionBadge && (
              <span className="card-promo-chip">{item.promotionBadge}</span>
            )}
            {item.description && item.description.trim() && (
              <p className="card-desc">{item.description}</p>
            )}
            {(optionsMeta.showOptionsLabel || optionsMeta.showCustomizable) && (
              <div className="menu-item-meta-badges">
                {optionsMeta.showOptionsLabel && (
                  <button
                    type="button"
                    className="menu-item-meta-badge"
                    onClick={() => optionsMeta.needsPicker && openOptionsSheet()}
                  >
                    Options ({optionsMeta.sizeCount})
                  </button>
                )}
                {optionsMeta.showCustomizable && (
                  <button
                    type="button"
                    className="menu-item-meta-badge menu-item-meta-badge--custom"
                    onClick={() => optionsMeta.needsPicker && openOptionsSheet()}
                  >
                    Customizable
                  </button>
                )}
              </div>
            )}
            {controls}
          </>
        ) : (
          <>
            {/* No-image layout: [veg dot] [name] [bookmark] on one row */}
            <div className="card-no-image-header">
              <span
                className={`card-veg-dot card-veg-dot--inline ${item.itemType === 'nonveg' ? 'non-veg' : ''}`}
                aria-label={item.itemType === 'veg' ? 'Vegetarian' : 'Non-vegetarian'}
              />
              <div className="card-name menu-item-title card-name--inline">
                <Link to={`/restaurants/${restaurant.appName}/menu/${item.itemUrl || item.id}`}>
                  {item.item}
                </Link>
              </div>
              <button
                type="button"
                className={`card-bookmark card-bookmark--inline wishlist-btn ${item.inWishlist ? 'saved active' : ''}`}
                onClick={() => onToggleWishlist(item.id, item.inWishlist || false, item.wishlistId)}
                aria-label={item.inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                title="Save"
              >
                {item.inWishlist ? <IconBookmarkFilled size={14} /> : <IconBookmark size={14} />}
              </button>
            </div>
            {item.tags && item.tags.length > 0 && (() => {
              const rt = resolveTag(item.tags![0]);
              return (
                <span className="card-tag-pill" style={{ background: rt.bg, color: rt.color, borderColor: rt.border }}>
                  <span>{rt.label}</span>
                  {rt.Icon && <rt.Icon size={10} />}
                </span>
              );
            })()}
            {item.promotionBadge && (
              <span className="card-promo-chip">{item.promotionBadge}</span>
            )}
            {item.description && item.description.trim() && (
              <p className="card-desc">{item.description}</p>
            )}
            {(optionsMeta.showOptionsLabel || optionsMeta.showCustomizable) && (
              <div className="menu-item-meta-badges">
                {optionsMeta.showOptionsLabel && (
                  <button
                    type="button"
                    className="menu-item-meta-badge"
                    onClick={() => optionsMeta.needsPicker && openOptionsSheet()}
                  >
                    Options ({optionsMeta.sizeCount})
                  </button>
                )}
                {optionsMeta.showCustomizable && (
                  <button
                    type="button"
                    className="menu-item-meta-badge menu-item-meta-badge--custom"
                    onClick={() => optionsMeta.needsPicker && openOptionsSheet()}
                  >
                    Customizable
                  </button>
                )}
              </div>
            )}
            {/* Price + controls on same row */}
            <div className="card-no-image-footer">
              <MenuItemPriceBlock
                effectivePrice={effectivePrice}
                strikeThroughPrice={strikeThroughPrice}
                discountPercentage={discountPercentage}
                sellingPrice={sellingPrice}
                formatPrice={formatPrice}
              />
              {controls}
            </div>
          </>
        )}
      </div>

      <MenuItemOptionsSheet
        isOpen={cart.sheetOpen}
        item={item}
        appName={restaurant.appName}
        selections={selections}
        onChange={cart.setSelections}
        onClose={cart.closeSheet}
        onConfirm={cart.confirmSheet}
        formatPrice={formatPrice}
        error={cart.sheetError}
        busy={cart.sheetBusy}
        confirmLabel={quantity === 0 ? 'Add to cart' : 'Add item'}
      />
      <MenuLinePrompt
        prompt={cart.prompt}
        itemName={item.item}
        cartPath={`/restaurants/${restaurant.appName}/cart`}
        onClose={cart.closePrompt}
        onRepeat={cart.repeatLast}
        onChooseNew={cart.chooseNew}
      />
    </div>
  );
};

export default MenuItemCard;
